import router from '../router';
import { useAuthStore } from '../stores/auth';
import type { FailedStep, FrameworksResponse, ModelMetadata, ProjectStatusResponse } from '../types';

/**
 * Fetch with automatic auth header injection (including impersonation if active)
 */
export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const authStore = useAuthStore();
  const authHeaders = await authStore.getAuthHeaders();

  const headers = new Headers(options.headers);
  for (const [key, value] of Object.entries(authHeaders)) {
    headers.set(key, value);
  }

  const response = await fetch(url, { ...options, headers });

  // Handle auth errors globally - but not when impersonating (timing issue on new window load)
  if (response.status === 401 && !sessionStorage.getItem('impersonate')) {
    await authStore.signOut();
    router.push('/auth');
  }

  if (response.status === 403) {
    throw new Error('FORBIDDEN');
  }

  return response;
}

export async function getProjectStatus(projectId: string): Promise<ProjectStatusResponse> {
  const response = await fetchWithAuth(`/api/projects/${projectId}/status`);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('PROJECT_NOT_FOUND');
    }
    throw new Error('STATUS_REQUEST_FAILED');
  }
  return await response.json();
}

export async function getFrameworks(): Promise<FrameworksResponse> {
  const response = await fetch('/api/frameworks');
  if (!response.ok) {
    throw new Error('Failed to load frameworks');
  }
  return await response.json();
}

export async function getModels(): Promise<{ models: ModelMetadata[] }> {
  const response = await fetch('/api/models');
  if (!response.ok) {
    throw new Error('Failed to load models');
  }
  return await response.json();
}

export async function getDebugInfo(projectId: string): Promise<{
  manuscript?: string;
  costs?: any;
  frameworkIntermediateOutputs?: Record<string, { name: string; content: string }[]>;
  stepErrors?: FailedStep[];
}> {
  const response = await fetchWithAuth(`/api/projects/${projectId}/debug`);
  if (!response.ok) {
    throw new Error('Failed to fetch debug information');
  }
  return await response.json();
}

export interface RecreatePlanStep {
  frameworkId: string;
  step: number;
  name: string;
  output: string;
  cascaded: boolean;
}

/**
 * Admin: Recreate specific outputs for a completed project.
 * This rehydrates the Durable Object from R2 and re-runs the steps that produce
 * these outputs. Returns the step plan (selected steps plus any pulled in by
 * the dependency cascade). Pass dryRun to get the plan without running anything.
 */
export async function recreateOutputs(
  projectId: string,
  outputs: string[],
  model?: string,
  dryRun?: boolean
): Promise<RecreatePlanStep[]> {
  const response = await fetchWithAuth(`/api/admin/projects/${projectId}/recreate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ outputs, ...(model && { model }), ...(dryRun && { dryRun: true }) }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to start recreation' }));
    throw new Error(error.error || 'Failed to start recreation');
  }
  const data = (await response.json()) as { steps?: RecreatePlanStep[] };
  return data.steps ?? [];
}

/**
 * Admin: Force-stop a stuck Durable Object by clearing its processing state.
 * The completed edit data in R2 is untouched.
 */
export async function resetProject(projectId: string): Promise<void> {
  const response = await fetchWithAuth(`/api/admin/projects/${projectId}/reset`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to reset project' }));
    throw new Error(error.error || 'Failed to reset project');
  }
}

/**
 * Delete a project (owner or admin). Soft delete: hidden immediately,
 * hard-purged after 30 days, restorable by support until then.
 */
export async function deleteProject(projectId: string): Promise<void> {
  const response = await fetchWithAuth(`/api/projects/${projectId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete project' }));
    throw new Error(error.error || 'Failed to delete project');
  }
}

/**
 * Admin: restore a soft-deleted project within the 30-day window.
 */
export async function restoreProject(projectId: string): Promise<void> {
  const response = await fetchWithAuth(`/api/admin/projects/${projectId}/restore`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to restore project' }));
    throw new Error(error.error || 'Failed to restore project');
  }
}

export interface FeedbackData {
  frameworkId: string;
  outputName: string;
  reaction: 'up' | 'down';
  message?: string | undefined;
}

export interface FeedbackItem {
  id: string;
  projectId: string;
  uid: string;
  frameworkId: string;
  outputName: string;
  reaction: string;
  message: string | null;
  createdAt: string;
}

export async function submitFeedback(projectId: string, data: FeedbackData): Promise<{ id: string }> {
  const response = await fetchWithAuth(`/api/projects/${projectId}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error('Failed to submit feedback');
  }
  return response.json();
}

export async function getProjectFeedback(projectId: string): Promise<{ feedback: FeedbackItem[] }> {
  const response = await fetchWithAuth(`/api/projects/${projectId}/feedback`);
  if (!response.ok) {
    throw new Error('Failed to fetch feedback');
  }
  return response.json();
}
